import {
  toFetchUsers,
  toFetchTeams,
  toFetchUsersWithDeleted,
} from "@/src/utils/userUtils.js";

export default {
  namespaced: true,

  state: {
    users: null,
    usersWithDeleted: null,
    teams: null,
    loadingUsers: false,
    loadingUsersWithDeleted: false,
    loadingTeams: false,
    systemUsers: [{ id: 0, name: "SuperAGI" }],
    presence: {},
  },

  mutations: {
    SET_USERS: (state, value) => {
      state.users = value;
    },

    SET_USERS_WITH_DELETED: (state, value) => {
      state.usersWithDeleted = value;
    },

    SET_TEAMS: (state, value) => {
      state.teams = value;
    },

    UPDATE_USER: (state, updatedUser) => {
      if (!state.users || !updatedUser?.id) return;
      const index = state.users.findIndex((u) => Number(u.id) === Number(updatedUser.id));
      if (index !== -1) {
        state.users = [
          ...state.users.slice(0, index),
          { ...state.users[index], ...updatedUser },
          ...state.users.slice(index + 1),
        ];
      }
    },

    SET_LOADING_USERS: (state, value) => {
      state.loadingUsers = value;
    },

    SET_LOADING_USERS_WITH_DELETED: (state, value) => {
      state.loadingUsersWithDeleted = value;
    },

    SET_LOADING_TEAMS: (state, value) => {
      state.loadingTeams = value;
    },

    SET_USER_PRESENCE: (state, { userId, is_online, last_seen }) => {
      state.presence = {
        ...state.presence,
        [userId]: { is_online, last_seen },
      };
    },

    SET_BULK_PRESENCE: (state, presenceMap) => {
      state.presence = { ...state.presence, ...presenceMap };
    },

    CLEAR_PRESENCE: (state) => {
      state.presence = {};
    },
  },

  actions: {
    async fetchUsers({ commit }, extra = []) {
      commit("SET_LOADING_USERS", true);

      try {
        const users = await toFetchUsers();
        const usersList = users || [];

        commit("SET_USERS", [...extra, ...usersList]);
        commit("SET_LOADING_USERS", false);
      } catch (error) {
        console.error("Error fetching users:", error);
        commit("SET_USERS", []);
        commit("SET_LOADING_USERS", false);
      }
    },

    async fetchUsersWithDeleted({ commit, state }, extra = []) {
      if (state.loadingUsersWithDeleted) return;
      if (state.usersWithDeleted !== null && extra.length === 0) return;

      commit("SET_LOADING_USERS_WITH_DELETED", true);

      try {
        const users = await toFetchUsersWithDeleted();
        const usersList = users || [];
        const processedUsers = usersList.map((user) => ({
          ...user,
          name: user.is_owner_deleted
            ? `${user.name} (Removed user)`
            : user.name,
          isDeleted: user.is_owner_deleted,
        }));

        commit("SET_USERS_WITH_DELETED", [...extra, ...processedUsers]);
        commit("SET_LOADING_USERS_WITH_DELETED", false);
      } catch (error) {
        console.error("Error fetching users with deleted:", error);
        commit("SET_USERS_WITH_DELETED", []);
        commit("SET_LOADING_USERS_WITH_DELETED", false);
      }
    },

    async fetchTeams({ commit }, extra = []) {
      commit("SET_LOADING_TEAMS", true);

      return toFetchTeams()
        .then((teams) => {
          const teamsList = teams || [];

          commit("SET_TEAMS", [...extra, ...teamsList]);
          commit("SET_LOADING_TEAMS", false);
        })
        .catch((error) => {
          console.error("Error fetching teams:", error);
          commit("SET_TEAMS", []);
          commit("SET_LOADING_TEAMS", false);
        });
    },

    fetchUsersAndTeams({ dispatch }) {
      return Promise.all([dispatch("fetchUsers"), dispatch("fetchTeams")]);
    },
  },

  getters: {
    users: (state) => state.users,
    allUsers: (state) => [...state.systemUsers, ...(state.users || [])],
    teams: (state) => state.teams,
    usersWithDeleted: (state) => state.usersWithDeleted,
    loadingUsers: (state) => state.loadingUsers,
    loadingTeams: (state) => state.loadingTeams,
    accessOptionsByTab: (state) => {
      const users =
        state.users?.map((user) => ({
          label: user.name,
          value: user.id,
        })) || [];

      const teams =
        state.teams?.map((team) => ({
          label: team.name,
          value: team.id,
        })) || [];

      return { Users: users, Teams: teams };
    },
    loadingUsersWithDeleted: (state) => state.loadingUsersWithDeleted,
    presence: (state) => state.presence,
    isUserOnline: (state) => (userId) => {
      return !!state.presence[userId]?.is_online;
    },
    userPresence: (state) => (userId) => {
      return state.presence[userId] || null;
    },
    resolveCommitAuthor: (state) => (committerID) => {
      if (!committerID) return { id: null, name: "Unknown", profile_pic: null };
      const found = (state.users || []).find(
        (u) => String(u.id) === String(committerID),
      );
      if (found) {
        return {
          id: found.id,
          name: found.name || found.email || String(committerID),
          profile_pic: found.profile_pic || null,
        };
      }
      return { id: null, name: String(committerID), profile_pic: null };
    },
    resolveCommitAuthorByEmail: (state) => (email) => {
      if (!email) return { id: null, name: "Unknown", profile_pic: null };
      const found = (state.users || []).find(
        (u) => u.email && u.email.toLowerCase() === email.toLowerCase(),
      );
      if (found) {
        return {
          id: found.id,
          name: found.name || found.email,
          profile_pic: found.profile_pic || null,
        };
      }
      return { id: null, name: email, profile_pic: null };
    },
  },
};
